/**
 * Columns-step (S4.T3b) action bodies, split out of `dataImportStore.actions.ts`
 * to keep both files at/under the ≤200-line rule. `setIdentityColumn`,
 * `resetColumnOverrides`, and `reprojectNativeTypes` all mutate a COMMITTED
 * entry (`target` already set) without going through the draft-commit
 * machinery: `setIdentityColumn` patches `target` directly (a Generated PK
 * column is synthetic — it needs no re-projection and must never re-fire
 * `rematch`), `resetColumnOverrides` clears user edits back to the latest
 * analysis, and `reprojectNativeTypes` re-hydrates `projectedNativeTypes` for
 * a restored session (see its own doc below).
 *
 * `hydrateEntryAction`/`setColumnOverrideAction`/`importAnotherFileAction`/
 * `removeTabEntries` are NOT column-specific — they were moved here (rather
 * than `dataImportStore.actions.ts`, which has less headroom) purely to keep
 * `dataImportStore.ts` itself at/under the 200-line cap; each is a direct
 * extraction of that store action's previous inline body, no behavior change.
 */
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { getEngineFromConnectionId } from '../../data-compare/utils/engineUtils';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { ImportTargetPlan } from '../../../../types/electron';
import type { DataImportColumnOverride, DataImportTabContent } from '../../core/types/workspaceTypes';
import {
  applyOverrides,
  clearFileScopedFields,
  contentFields,
  fetchNativeTypes,
  PROJECT_TYPES_ERROR_MESSAGE,
  resolveEntryEngine,
} from './dataImportStore.actions';
import type { DataImportEntry } from './dataImportStore';

/** `hydrate`'s body — seeds (or re-seeds) a tab's entry from its persisted/restored tab content. */
export function hydrateEntryAction(
  entries: Record<string, DataImportEntry>,
  tabId: string,
  content?: DataImportTabContent,
): DataImportEntry {
  const existing = entries[tabId];
  const fields = contentFields(content);
  if (existing) return { ...existing, ...fields };
  return {
    ...fields,
    // Open-time Target prefill (`openDataImportTab`'s connection seed): FRESH entries only —
    // re-hydrates must never clobber a live draft, and a committed `target` must not spawn a
    // diverging draft (the "draft changed" status line would misfire).
    draftTarget: fields.target ? undefined : content?.draftTarget,
    analysis: undefined,
    columns: [],
  };
}

/** `setColumnOverride`'s body — upserts one column's override by `sourceName` and re-derives `columns`. */
export function setColumnOverrideAction(
  entry: DataImportEntry,
  sourceName: string,
  patch: Partial<Omit<DataImportColumnOverride, 'sourceName'>>,
): DataImportEntry {
  const others = entry.columnOverrides.filter((o) => o.sourceName !== sourceName);
  const previous = entry.columnOverrides.find((o) => o.sourceName === sourceName);
  const columnOverrides = [...others, { sourceName, ...previous, ...patch }];
  const columns = entry.analysis
    ? applyOverrides(entry.analysis.columns, columnOverrides, entry.projectedNativeTypes, resolveEntryEngine(entry))
    : entry.columns;
  return { ...entry, columnOverrides, columns };
}

/** `importAnotherFile`'s body — clears the file + analysis but keeps the destination `target` and
 * the `sourceName`-keyed column overrides (a new file's matching headers reuse them). Also drops
 * the file-scoped `rowFilter`/`mappings`/`parse.sheetName` (finding #7 — index/sheet-keyed to the
 * OLD file, they'd silently target unrelated columns on the next file) via `clearFileScopedFields`,
 * and clears `projectedNativeTypes` (I3 fix): it's derived from the PREVIOUS file's canonical types,
 * not a user choice — keeping it would let a same-named header reuse a stale projected native type
 * in CREATE TABLE. It's re-populated fresh on the next Target commit/reproject. */
export function importAnotherFileAction(entry: DataImportEntry): DataImportEntry {
  return {
    ...entry,
    ...clearFileScopedFields(entry),
    filePath: undefined,
    analysis: undefined,
    columns: [],
    projectedNativeTypes: undefined,
    step: 'source',
  };
}

/** `removeTab`'s body — drops only the requested tab's entry from the map. */
export function removeTabEntries(
  entries: Record<string, DataImportEntry>,
  tabId: string,
): Record<string, DataImportEntry> {
  const rest: Record<string, DataImportEntry> = {};
  for (const [id, entry] of Object.entries(entries)) {
    if (id !== tabId) rest[id] = entry;
  }
  return rest;
}

/** Clears ALL `columnOverrides` (keeps `projectedNativeTypes` — a native-type
 * reset is a separate concern from a destination re-commit) and re-derives
 * `columns` from the latest analysis. Powers the `•••` "Reset column changes". */
export function resetColumnOverridesAction(entry: DataImportEntry): DataImportEntry {
  return {
    ...entry,
    columnOverrides: [],
    columns: applyOverrides(entry.analysis?.columns ?? entry.columns, [], entry.projectedNativeTypes, resolveEntryEngine(entry)),
  };
}

/** Bulk-restores `columnOverrides` wholesale — the Undo side of the `•••`
 * "Reset column changes" toast (S4.T3b-review G1): `ColumnsStep` snapshots
 * `columnOverrides` before calling `resetColumnOverrides`, then this action
 * replaces them in one shot and re-derives `columns`, mirroring
 * `resetColumnOverridesAction`'s derivation with a caller-supplied array
 * instead of `[]`. */
export function restoreColumnOverridesAction(
  entry: DataImportEntry,
  overrides: DataImportColumnOverride[],
): DataImportEntry {
  return {
    ...entry,
    columnOverrides: overrides,
    columns: applyOverrides(entry.analysis?.columns ?? entry.columns, overrides, entry.projectedNativeTypes, resolveEntryEngine(entry)),
  };
}

/** Patches the COMMITTED `target` directly — a Generated PK column is a
 * synthetic `int32` identity, so it needs no `projectTypes` re-projection and
 * must NOT re-fire `rematch` (nothing about the source columns changed).
 * No-op if `target` is undefined (Generated PK only exists post-commit). */
export function setIdentityColumnAction(
  entry: DataImportEntry,
  patch: { addIdentityColumn?: boolean; identityColumnName?: string },
): DataImportEntry {
  if (!entry.target) return entry;
  return { ...entry, target: { ...entry.target, ...patch } };
}

/** Patches the COMMITTED `target`'s `truncateFirst` flag directly (S6.T2c-3) — the Import step's
 * truncate-first checkbox, not a Target-step destination edit, so it bypasses the draft-commit
 * machinery the exact same way `setIdentityColumnAction` does. No-op if `target` is undefined. */
export function setTruncateFirstAction(entry: DataImportEntry, truncateFirst: boolean): DataImportEntry {
  if (!entry.target) return entry;
  return { ...entry, target: { ...entry.target, truncateFirst } };
}

// Guards `reprojectNativeTypesAction` against re-entrancy per tab — a second
// mount-effect call (e.g. React StrictMode) while the first is still in
// flight is a no-op rather than a duplicate IPC round-trip.
const reprojectionsInFlight = new Set<string>();

/**
 * Closes a T3a-review gap (⚠️): `projectedNativeTypes` is never persisted, so
 * a RESTORED session has a committed `target` but an empty
 * `projectedNativeTypes` — Columns would show canonical labels, not
 * engine-native types, until the user re-commits Target. `ColumnsStep` calls
 * this once on mount when `target` is set, `projectedNativeTypes` is empty,
 * and `columns.length > 0`. Re-runs `projectTypes` for the committed target's
 * engine over the CURRENT columns and repopulates `projectedNativeTypes` +
 * re-derives `columns` in one `set()` — WITHOUT touching `target` and WITHOUT
 * firing `rematch` (nothing about the destination changed, only the missing
 * projection is being backfilled).
 */
export async function reprojectNativeTypesAction(
  tabId: string,
  entry: DataImportEntry,
  applyResult: (build: (current: DataImportEntry) => DataImportEntry) => void,
): Promise<void> {
  const target: ImportTargetPlan | undefined = entry.target;
  if (!target) return;
  if (entry.projectedNativeTypes && Object.keys(entry.projectedNativeTypes).length > 0) return;
  if (entry.columns.length === 0) return;
  if (reprojectionsInFlight.has(tabId)) return;

  reprojectionsInFlight.add(tabId);
  try {
    const { connections, activeConnections } = useConnectionStore.getState();
    const engine: DatabaseEngineId =
      getEngineFromConnectionId(target.connectionId, connections, activeConnections) ?? 'mock';

    const columnsAtRequestTime = entry.columns;
    const canonicals = columnsAtRequestTime.map((c) => c.canonical);

    const nativeTypes = await fetchNativeTypes(engine, canonicals);
    if (!nativeTypes) {
      applyResult((current) => ({ ...current, projectTypesError: PROJECT_TYPES_ERROR_MESSAGE }));
      return;
    }

    applyResult((current) => {
      const projectedNativeTypes: Record<string, string> = {};
      columnsAtRequestTime.forEach((col, i) => {
        const native = nativeTypes[i];
        if (native !== undefined) projectedNativeTypes[col.sourceName] = native;
      });
      const columns = applyOverrides(
        current.analysis?.columns ?? current.columns,
        current.columnOverrides,
        projectedNativeTypes,
        engine,
      );
      return { ...current, projectedNativeTypes, columns, projectTypesError: null };
    });
  } finally {
    reprojectionsInFlight.delete(tabId);
  }
}
